import {
  GraphQLErrorCode,
  GraphQLInternalErrorCode,
} from '@this+that/constants';

import { getGraphQLErrorCode, getInternalErrorCode } from '../graphqlErrors';

const DELEGATE_ERRORS_ROOT = 'doBox.delegation.errors';

export const DELEGATE_FAILED_I18N_KEY = `${DELEGATE_ERRORS_ROOT}.delegateFailed`;
export const ALREADY_DELEGATED_I18N_KEY = `${DELEGATE_ERRORS_ROOT}.alreadyDelegated`;

const INTERNAL_CODE_I18N_KEY: Partial<
  Record<GraphQLInternalErrorCode, string>
> = {
  [GraphQLInternalErrorCode.AGENT_RUN_LIMIT_REACHED]: `${DELEGATE_ERRORS_ROOT}.runLimitReached`,
  [GraphQLInternalErrorCode.AGENT_RUNS_ITEM_TOO_LARGE]: `${DELEGATE_ERRORS_ROOT}.runsItemTooLarge`,
  [GraphQLInternalErrorCode.LOCAL_RUN_ALREADY_ACTIVE_ON_DEVICE]: `${DELEGATE_ERRORS_ROOT}.localRunAlreadyActive`,
  [GraphQLInternalErrorCode.EXECUTOR_RUN_ALREADY_ACTIVE]: `${DELEGATE_ERRORS_ROOT}.executorRunAlreadyActive`,
};

interface LegacyGraphQLErrorShape {
  graphQLErrors?: {
    extensions?: { code?: string; internalErrorCode?: string };
  }[];
}

function readLegacyExtensions(error: unknown) {
  return (error as LegacyGraphQLErrorShape | null)?.graphQLErrors ?? [];
}

function isConflict(error: unknown): boolean {
  return readLegacyExtensions(error).some(
    (e) => e.extensions?.code === GraphQLErrorCode.CONFLICT,
  );
}

function readInternalCode(error: unknown): GraphQLInternalErrorCode | null {
  const code = getInternalErrorCode(error);
  if (code) {
    return code;
  }
  const legacy = readLegacyExtensions(error).find(
    (e) => e.extensions?.internalErrorCode,
  );
  return (legacy?.extensions?.internalErrorCode ??
    null) as GraphQLInternalErrorCode | null;
}

/** The message for a failed "delegate" call: the specific refusal the API names, the generic
 *  "already handled" for a bare conflict (flag off, an active run), else the generic failure. */
export function resolveDelegateErrorI18nKey(error: unknown): string {
  const internalCode = readInternalCode(error);
  const specificKey = internalCode
    ? INTERNAL_CODE_I18N_KEY[internalCode]
    : null;
  if (specificKey) {
    return specificKey;
  }
  return isConflict(error) ||
    getGraphQLErrorCode(error) === GraphQLErrorCode.CONFLICT
    ? ALREADY_DELEGATED_I18N_KEY
    : DELEGATE_FAILED_I18N_KEY;
}
